import { t } from 'utils/getTranslations';
import { ReportTone, ToneRule } from './reportCells';

/** A figure the report colours, by its column header or measure, and the rule its cells take. */
export type ToneLegendEntry = { label: string; rule: ToneRule };

type LegendColumn = { header?: unknown; cell?: unknown; columns?: readonly LegendColumn[] };

const TONES: ReportTone[] = ['green', 'yellow', 'red'];
const STEPS = [1, 2, 3, 4, 5];

/**
 * The coloured columns of a table: every column whose cell renderer came from `tonedCell`, which carries its rule.
 * A column with a header that is not plain text is left out, as the legend has no name for it.
 */
export function toneLegendOf(columns: readonly LegendColumn[]): ToneLegendEntry[] {
    return columns.flatMap((column) => {
        if (column.columns) return toneLegendOf(column.columns);
        const rule = (column.cell as { toneRule?: ToneRule } | undefined)?.toneRule;
        return rule && typeof column.header === 'string' ? [{ label: column.header, rule }] : [];
    });
}

/** One line for each colour the report uses: the figures it marks above zero, then the ones it marks below. */
export function toneLegendLines(entries: ToneLegendEntry[]) {
    return TONES.map((tone) => ({
        tone,
        above: entries.filter((entry) => entry.rule.above === tone).map((entry) => entry.label),
        below: entries.filter((entry) => entry.rule.below === tone).map((entry) => entry.label),
    })).filter((line) => line.above.length > 0 || line.below.length > 0);
}

/** The figures a colour marks below zero; when every coloured figure takes it, the key says so in short. */
function belowZeroText(labels: string[], entries: ToneLegendEntry[]): string {
    return labels.length === entries.length
        ? t('reporting.tone-legend.any-below-zero')
        : t('reporting.tone-legend.below-zero', { columns: labels.join(', ') });
}

/** The About panel's colour key: each colour's five shades and the figures it marks. */
export function ReportToneLegend({ entries }: { entries: ToneLegendEntry[] }) {
    const lines = toneLegendLines(entries);
    if (lines.length === 0) return null;
    return (
        <>
            <h3 className="m-0 mt-2 text-sm font-semibold">{t('reporting.tone-legend.heading')}</h3>
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                {lines.map((line) => {
                    const parts = [
                        ...(line.above.length > 0 ? [line.above.join(', ')] : []),
                        ...(line.below.length > 0 ? [belowZeroText(line.below, entries)] : []),
                    ];
                    return (
                        <li key={line.tone} className="flex items-start gap-3">
                            <span
                                aria-hidden
                                className="ring-foreground/10 mt-0.5 flex shrink-0 overflow-hidden rounded-sm ring-1"
                            >
                                {STEPS.map((step) => (
                                    <span
                                        key={step}
                                        className="size-3.5"
                                        style={{ backgroundColor: `var(--report-tone-${line.tone}-${step})` }}
                                    />
                                ))}
                            </span>
                            <span>
                                <span className="font-medium">{t(`reporting.tone-legend.${line.tone}`)}:</span>{' '}
                                {parts.join('; ')}
                            </span>
                        </li>
                    );
                })}
            </ul>
            <p className="text-muted-foreground m-0">{t('reporting.tone-legend.shade')}</p>
        </>
    );
}
